/**
 * The graph's corner buttons: zoom in, zoom out, the link tool, fit view, one button per view
 * offered here (nodal, class diagram, blueprint, board), then 2D/3D in the nodal view or "Arrange by
 * group" (an action) where boxes are drawn.
 */
import { For, Show, type JSX } from "solid-js";
import { VIEW_LABELS, type ViewMode } from "../../state/view";
import { useApp } from "../../app/context";
import { arrangeSelection } from "./diagram/arrange";

/** The zoom bar. `zoom` scales the view around its centre. */
export function ZoomBar(props: { zoom: (factor: number) => void }): JSX.Element {
  const { view, diagram } = useApp();
  return (
    <div class="zoom" classList={{ "on-document": view.mode() === "document" }}>
      <Show when={view.mode() !== "document"}>
      <button type="button" aria-label="Zoom in" onClick={() => props.zoom(1.25)}>
        +
      </button>
      <button type="button" aria-label="Zoom out" onClick={() => props.zoom(0.8)}>
        −
      </button>
      <Show when={view.mode() !== "board" && view.mode() !== "document"}>
        <button type="button" aria-label="Link tool" title="Link tool (L): drag from one node to another" aria-pressed={view.state.linkTool} onClick={() => view.setLinkTool(!view.state.linkTool)}>
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
            <circle cx="3.5" cy="12" r="2.2" />
            <circle cx="12.5" cy="4" r="2.2" />
            <path d="M5.2 10.6L10.8 5.4" />
          </svg>
        </button>
      </Show>
      <button type="button" aria-label="Fit view" title="Fit view (F)" onClick={() => view.requestFit()}>
        <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
          <path d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4" />
        </svg>
      </button>
      </Show>
      <Show when={view.views().length > 1}>
        <For each={view.views()}>
          {(mode) => (
            <button
              type="button"
              id={`graph-${mode}`}
              data-view={mode}
              aria-label={VIEW_LABELS[mode]}
              title={view.mode() === mode && mode !== "nodal" ? "Back to the nodal graph (D: the next view)" : `${VIEW_LABELS[mode]} (D: the next view)`}
              aria-pressed={view.mode() === mode}
              onClick={() => view.setMode(view.mode() === mode && mode !== "nodal" && view.views().includes("nodal") ? "nodal" : mode)}
            >
              <ViewIcon mode={mode} />
            </button>
          )}
        </For>
      </Show>
      <Show when={view.mode() === "diagram" || view.mode() === "blueprint"}>
        <button
          type="button"
          id="diagram-arrange"
          aria-label="Arrange by group"
          title={`${diagram.arrangeLabel()} (select a package, or boxes with Shift+drag; nothing selected: the whole diagram)`}
          onClick={() => void arrangeSelection(diagram)}
        >
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
            <rect x="1.5" y="1.5" width="6" height="6" />
            <rect x="8.5" y="1.5" width="6" height="6" />
            <rect x="1.5" y="8.5" width="13" height="6" />
          </svg>
        </button>
      </Show>
      <Show when={view.mode() === "nodal"}>
        <button
          type="button"
          id="graph-3d"
          class="dimension"
          aria-label="3D view"
          title={view.state.dimension === "3d" ? "Back to 2D (3)" : "3D view (3): spread a crowded graph in depth; drag the background to turn it, Ctrl+drag or the middle button to pan"}
          aria-pressed={view.state.dimension === "3d"}
          onClick={() => view.setDimension(view.state.dimension === "3d" ? "2d" : "3d")}
        >
          {view.state.dimension === "3d" ? "3D" : "2D"}
        </button>
      </Show>
    </div>
  );
}

/** A view's little picture: dots and lines, a UML box, a box with pins wired on, a sticky note and a frame, a sheet of paper. */
function ViewIcon(props: { mode: ViewMode }): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
      {props.mode === "nodal" ? (
        <>
          <circle cx="4" cy="4" r="2" />
          <circle cx="12" cy="6" r="2" />
          <circle cx="6" cy="12.5" r="2" />
          <path d="M5.8 4.6l4.3 1M5 10.6l-.4-4.6M10.4 7.4l-3 3.6" />
        </>
      ) : props.mode === "diagram" ? (
        <>
          <rect x="2" y="1.5" width="12" height="8" rx="0.5" />
          <path d="M2 4.5h12M8 9.5v3M5.5 14.5L8 12l2.5 2.5z" />
        </>
      ) : props.mode === "document" ? (
        <>
          <path d="M3.5 1.5h6l3 3v10h-9z" />
          <path d="M9.5 1.5v3h3M5.5 7.5h5M5.5 10h5M5.5 12.5h3" />
        </>
      ) : props.mode === "board" ? (
        <>
          <rect x="1.5" y="1.5" width="13" height="13" rx="1" stroke-dasharray="2 1.5" />
          <path d="M4 4.5h5v5H4z" />
          <path d="M9 12h3.5M7 9.5l3 2" />
        </>
      ) : (
        <>
          <rect x="1.5" y="3" width="7" height="10" rx="1" />
          <circle cx="8.5" cy="6.5" r="1.2" fill="currentColor" />
          <circle cx="8.5" cy="10" r="1.2" fill="currentColor" />
          <path d="M9.7 6.5c3 0 2-3 4.8-3M9.7 10c3 0 2 3 4.8 3" />
        </>
      )}
    </svg>
  );
}
